import { useEffect } from 'react';
import { View, StyleSheet, type DimensionValue } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  interpolate,
} from 'react-native-reanimated';
import { LinearGradient } from 'expo-linear-gradient';
import { useThemeColors } from '@/lib/useThemeColors';

interface SkeletonProps {
  width?: DimensionValue;
  height?: number;
  borderRadius?: number;
  style?: object;
}

/**
 * Base skeleton element with shimmer animation
 */
export function Skeleton({
  width = '100%',
  height = 16,
  borderRadius = 8,
  style,
}: SkeletonProps) {
  const colors = useThemeColors();
  const shimmerPosition = useSharedValue(0);

  useEffect(() => {
    shimmerPosition.value = withRepeat(
      withTiming(1, { duration: 1200 }),
      -1,
      false
    );
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateX: interpolate(
          shimmerPosition.value,
          [0, 1],
          [-200, 200]
        ),
      },
    ],
  }));

  return (
    <View
      style={[
        {
          width,
          height,
          borderRadius,
          backgroundColor: colors.surface.secondary,
          overflow: 'hidden',
        },
        style,
      ]}
    >
      <Animated.View style={[StyleSheet.absoluteFill, animatedStyle]}>
        <LinearGradient
          colors={[
            'transparent',
            `${colors.surface.primary}40`,
            'transparent',
          ]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
    </View>
  );
}

/**
 * Skeleton for a circular avatar
 */
export function SkeletonAvatar({ size = 40 }: { size?: number }) {
  return <Skeleton width={size} height={size} borderRadius={size / 2} />;
}

/**
 * Skeleton for text lines
 */
export function SkeletonText({
  lines = 1,
  lastLineWidth = '60%',
  lineHeight = 14,
  gap = 8,
}: {
  lines?: number;
  lastLineWidth?: number | `${number}%`;
  lineHeight?: number;
  gap?: number;
}) {
  return (
    <View style={{ gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          width={i === lines - 1 && lines > 1 ? lastLineWidth : '100%'}
          height={lineHeight}
          borderRadius={4}
        />
      ))}
    </View>
  );
}

/**
 * Skeleton card for dealflow list items
 */
export function DealflowSkeletonCard({ layout = 'list' }: { layout?: 'list' | 'grid' | 'compact' }) {
  const colors = useThemeColors();

  if (layout === 'compact') {
    return (
      <View
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 12,
          padding: 12,
          flexDirection: 'row',
          alignItems: 'center',
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <Skeleton width={40} height={40} borderRadius={8} />
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Skeleton width="70%" height={14} borderRadius={4} />
          <View style={{ height: 6 }} />
          <Skeleton width="50%" height={12} borderRadius={4} />
        </View>
        <Skeleton width={50} height={24} borderRadius={12} />
      </View>
    );
  }

  if (layout === 'grid') {
    return (
      <View
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 16,
          padding: 16,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
          <Skeleton width={56} height={56} borderRadius={12} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Skeleton width="80%" height={16} borderRadius={4} />
            <View style={{ height: 6 }} />
            <Skeleton width="60%" height={12} borderRadius={4} />
          </View>
        </View>

        {/* Tags */}
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <Skeleton width={70} height={24} borderRadius={8} />
          <Skeleton width={60} height={24} borderRadius={8} />
          <Skeleton width={80} height={24} borderRadius={8} />
        </View>

        {/* Score row */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton width={80} height={32} borderRadius={8} />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Skeleton width={32} height={32} borderRadius={8} />
            <Skeleton width={32} height={32} borderRadius={8} />
          </View>
        </View>
      </View>
    );
  }

  // Default: list layout
  return (
    <View
      style={{
        backgroundColor: colors.surface.primary,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border.light,
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <View style={{ padding: 12, flexDirection: 'row', alignItems: 'center' }}>
        <Skeleton width={40} height={40} borderRadius={8} />
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Skeleton width="60%" height={14} borderRadius={4} />
          <View style={{ height: 6 }} />
          <Skeleton width="40%" height={12} borderRadius={4} />
        </View>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <Skeleton width={50} height={24} borderRadius={8} />
          <Skeleton width={60} height={20} borderRadius={10} />
          <Skeleton width={18} height={18} borderRadius={4} />
        </View>
      </View>
    </View>
  );
}

/**
 * Skeleton for portfolio cards
 */
export function PortfolioSkeletonCard() {
  const colors = useThemeColors();

  return (
    <View
      style={{
        backgroundColor: colors.surface.primary,
        borderRadius: 12,
        padding: 12,
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <Skeleton width={40} height={40} borderRadius={20} />
      <View style={{ flex: 1, marginLeft: 12 }}>
        <Skeleton width="50%" height={14} borderRadius={4} />
        <View style={{ height: 6 }} />
        <Skeleton width="70%" height={12} borderRadius={4} />
      </View>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Skeleton width={32} height={32} borderRadius={8} />
        <Skeleton width={32} height={32} borderRadius={8} />
      </View>
    </View>
  );
}

/**
 * Skeleton for stats cards
 */
export function StatsSkeletonCard() {
  const colors = useThemeColors();

  return (
    <View
      style={{
        backgroundColor: colors.surface.primary,
        borderRadius: 16,
        padding: 16,
        width: 112,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        elevation: 3,
      }}
    >
      <Skeleton width={48} height={28} borderRadius={4} />
      <View style={{ height: 6 }} />
      <Skeleton width={64} height={12} borderRadius={4} />
    </View>
  );
}
